import { useCopy } from '@/hooks/useCopy';
import {
  ButtonV2,
  ButtonV2Props,
  IconApproval,
  IconCopy,
} from '@moneyforward/ap-frontend-components';
import { ReactNode, RefObject, forwardRef, memo, useCallback } from 'react';

export type CopyButtonWithLabelProps = Omit<
  ButtonV2Props,
  'onClick' | 'children' | 'leftIcon'
> & {
  text: string;
  show?: boolean;
  copyLabel: ReactNode;
  copiedLabel: ReactNode;
  resetMilliSecond?: number;
};

/**
 * Labeled counterpart of `CopyButton`: same copy / copied state handling, but
 * rendered as a `ButtonV2` with a visible label instead of an icon-only button.
 */
export const CopyButtonWithLabel = memo(
  forwardRef<HTMLButtonElement, CopyButtonWithLabelProps>(
    (
      {
        text,
        show = true,
        copyLabel,
        copiedLabel,
        resetMilliSecond = 1000,
        ...rest
      },
      ref
    ) => {
      const { copy, reset, copied } = useCopy(
        text,
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        ref as RefObject<HTMLButtonElement>
      );

      const onClick = useCallback(() => {
        copy();
        const timeoutId = setTimeout(reset, resetMilliSecond);
        return () => clearTimeout(timeoutId);
      }, [copy, reset, resetMilliSecond]);

      if (!show) return null;

      return (
        <ButtonV2
          ref={ref}
          onClick={onClick}
          leftIcon={copied ? <IconApproval size={0} /> : <IconCopy size={0} />}
          {...rest}
        >
          {copied ? copiedLabel : copyLabel}
        </ButtonV2>
      );
    }
  )
);
CopyButtonWithLabel.displayName = 'CopyButtonWithLabel';
